import { Alert, AlertDescription, Button } from '@zond/ui';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { getAppName, getLogo } from '../../../../utils/get-logo';
import { useInvitation } from '../hooks/useInvitation';
import { useAcceptInvitation, useRejectInvitation } from '../hooks/useInvitationMutations';
import { ResolvedInvitation } from '../types';

export function InvitationPage() {
  const { t } = useTranslation('invitations');
  const { invitationId } = useParams();
  const navigate = useNavigate();

  const { data: invitation, isLoading } = useInvitation(invitationId);
  const acceptMutation = useAcceptInvitation();
  const rejectMutation = useRejectInvitation();

  const handleAccept = async (invitation: ResolvedInvitation) => {
    try {
      await acceptMutation.mutateAsync(invitation.id);

      toast.success(t('toast.acceptSuccess', { workspace: invitation.workspace.name }));

      navigate(`/${invitation.workspaceId}/`);
    } catch (err) {
      toast.error(t('toast.acceptError', { workspace: invitation.workspace.name }));
      navigate('/');
    }
  };

  const handleReject = async (invitation: ResolvedInvitation) => {
    try {
      await rejectMutation.mutateAsync(invitation.id);

      toast.success(t('toast.rejectSuccess'));

      navigate('/');
    } catch (err) {
      toast.error(t('toast.rejectError'));
      navigate('/');
    }
  };

  if (isLoading) {
    return <div>{t('common:states.loading')}</div>;
  }

  if (!invitation) {
    return <div>{t('states.notFound')}</div>;
  }

  return (
    <div className="flex min-h-screen">
      <div className="flex flex-col w-full max-w-sm mx-auto px-4 lg:w-96 justify-center">
        <div>
          <img className="h-12 w-auto" src={getLogo()} alt={t('page.logoAlt', { appName: getAppName() })} />
          <h2 className="mt-6 text-2xl font-bold text-gray-900">
            {t('page.title', { workspace: invitation.workspace.name })}
          </h2>
          <p className="mt-2 text-sm text-gray-600">{t('page.subtitle')}</p>

          <div className="flex gap-4 mt-8">
            <Button
              variant="default"
              onClick={() => handleAccept(invitation)}
              disabled={acceptMutation.isPending || rejectMutation.isPending}
            >
              {acceptMutation.isPending ? t('actions.accepting') : t('actions.accept')}
            </Button>
            <Button
              variant="outline"
              onClick={() => handleReject(invitation)}
              disabled={acceptMutation.isPending || rejectMutation.isPending}
            >
              {rejectMutation.isPending ? t('actions.declining') : t('actions.decline')}
            </Button>
          </div>

          {(acceptMutation.error || rejectMutation.error) && (
            <Alert variant="destructive" className="mt-4">
              <AlertDescription>
                {acceptMutation.error
                  ? t('errors.accept', { workspace: invitation.workspace.name })
                  : t('errors.reject')}
              </AlertDescription>
            </Alert>
          )}
        </div>
      </div>
    </div>
  );
}
